import _ from "lodash";
import * as React from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { Button } from "src/library/components/Button";
import { Modal } from "src/library/components/Modal";
import { Text } from "src/library/components/Text";
import Grid from "src/library/components/Hedron";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import {
  FixedBottomMenu,
  FixedBottomMenuWrapper
} from "src/core/shared/components/menus/FixedBottomMenu";
import {
  IPromotionalTag,
  PromotionalTagRequestName,
  usePromotionalTagApi
} from "src/modules/promotions/api/promotional-tags.api";
import { PromotionTagDraggableCard } from "src/modules/promotions/components/promotional-tag/PromotionalTagDraggableCard";
import { PromotionTagUneditableCard } from "src/modules/promotions/components/promotional-tag/PromotionalTagUneditableCard";
import { LikeToDo } from "src/library/components/LikeToDo";

/**
 * Main Promotional Tags View
 *
 * @returns
 */
const ViewAllPromotionalTagsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const [ getPromotionTagsResponse, getPromotionTags ] = usePromotionalTagApi(PromotionalTagRequestName.GET_PROMOTIONAL_TAGS);
  const [ deletePromotionTagsResponse, deletePromotionTags ] = usePromotionalTagApi(PromotionalTagRequestName.DELETE_PROMOTIONAL_TAG);
  const [ bulkUpdatePromotionTagsPriorityRes, bulkUpdatePromotionTagsPriorityReq ] = usePromotionalTagApi(PromotionalTagRequestName.BULK_UPDATE_PROMOTIONAL_TAG);
  const [ currentPromotionTagsData, setCurrentPromotionTagsData ] = React.useState<IPromotionalTag[]>([]);
  const [ showModal, setShowModal ] = React.useState<IPromotionalTag | null>(null);
  const [ priorityChanged, setPriorityChanged ] = React.useState(false);
  const checkPermission = useCheckUserPermission();
  const canAmendPromo = checkPermission("amend-promotion");

  React.useEffect(() => {
    // get promotional tags on mount
    getPromotionTags({});
  }, [ getPromotionTags ]);

  React.useEffect(() => {
    if (getPromotionTagsResponse.data) {
      // ensure order by priority then sanitise priority values
      setCurrentPromotionTagsData(_.map(_.orderBy(getPromotionTagsResponse.data, promotionalTag => promotionalTag.priority), (promotionalTag, index) => ({
        ...promotionalTag,
        priority: index + 1
      })));
    }
  }, [ getPromotionTagsResponse, getPromotionTagsResponse.data ]);

  React.useEffect(() => {
    // on success reload promotional tag data
    if (deletePromotionTagsResponse.data) {
      getPromotionTags({});
      toast.success("Promotional tag has been successfully deleted.");
    }
  }, [ deletePromotionTagsResponse, getPromotionTags ]);

  React.useEffect(() => {
    // handle a bulk promotinoal tag priority order api response
    if (bulkUpdatePromotionTagsPriorityRes.data) {
      toast.success("Promotional tags priority order saved!");
      setPriorityChanged(false);
    } else if (bulkUpdatePromotionTagsPriorityRes.error) {
      toast.error(bulkUpdatePromotionTagsPriorityRes.error.message);
    }
  }, [ bulkUpdatePromotionTagsPriorityRes ]);

  // update promotion tag sort orders when dragging a card
  const moveCard = React.useCallback((dragIndex: number, hoverIndex: number) => {
    setCurrentPromotionTagsData(promotionTags => {
      if (dragIndex) {
        promotionTags[ hoverIndex - 1 ].priority = dragIndex;
        promotionTags[ dragIndex - 1 ].priority = hoverIndex;

        return _.orderBy(promotionTags, promotionTag => promotionTag.priority);
      }

      return promotionTags;
    });
  }, []);

  return (
    <Layout {...props}>
      <MainContentWrapper
        fill
        Header={canAmendPromo && (
          <NewPromotionTagWrapper>
            <LikeToDo
              copy="What would you like to do?"
              buttons={[
                (
                  <Button
                    key="create-promotional-tag"
                    onClick={() => props.history.push("/admin/promotions/promotional-tags/create")}
                  >
                    Create new promotional tag
                  </Button>
                )
              ]}
            />
          </NewPromotionTagWrapper>
        )}
      >
        <Grid.Bounds style={{ flexWrap: "wrap" }}>
          <PromotionTagWrapper fill>
            {getPromotionTagsResponse?.data?.length ? (
              <Text as="p">
                {canAmendPromo ? "Drag and Drop to reorder the tags to determine the priority. " : " "}
                Only one tag displays on a product at any given time.
              </Text>
            ) : (
              <Text as="p">
                There are currently no promotional tags
              </Text>
            )}

            {getPromotionTagsResponse.pending && (
              <LoadingSpinner
                type="flat"
                position="inline"
              />
            )}

            <DndProvider backend={HTML5Backend}>
              <PromotionTagList>
                {canAmendPromo ?
                  _.map(currentPromotionTagsData, promotionTag => (
                    <PromotionTagDraggableCard
                      key={promotionTag.id}
                      id={promotionTag.id}
                      label={promotionTag.label}
                      color={promotionTag.color}
                      priority={promotionTag.priority}
                      moveCard={moveCard}
                      priorityChanged={priorityChanged}
                      updatePriorityChanged={setPriorityChanged}
                      onDeletePromotionTag={promotionTag => setShowModal(promotionTag)}
                    />
                  )) :
                  _.map(currentPromotionTagsData, promotionTag => (
                    <PromotionTagUneditableCard
                      id={promotionTag.id}
                      label={promotionTag.label}
                      color={promotionTag.color}
                    />
                  ))}
              </PromotionTagList>
            </DndProvider>

          </PromotionTagWrapper>

        </Grid.Bounds>

        <FixedBottomMenuWrapper active={priorityChanged}>
          <FixedBottomMenu
            onCloseText="Discard Changes"
            onClose={() => {
              setPriorityChanged(false);
              getPromotionTags({});
            }}
            actions={[
              {
                $buttonType: "primary",
                loading: bulkUpdatePromotionTagsPriorityRes ? false : true,
                name: "Save Promotional Tags",
                onClick: () => bulkUpdatePromotionTagsPriorityReq({ data: currentPromotionTagsData })
              }
            ]}
          />
        </FixedBottomMenuWrapper>

        <Modal
          small
          show={showModal !== null}
          type="confirm"
          title="Are you sure?"
          body={showModal ? `Please confirm that you wish to permanently delete the ${showModal?.label} promotion tag` : "no tag selected for deletion"}
          actions={[
            {
              name: "No, cancel",
              $buttonType: "secondary",
              action: () => setShowModal(null)
            },
            {
              name: "I confirm",
              $buttonType: "primary",
              action: () => {
                if (showModal?.id !== undefined) {
                  setShowModal(null);
                  deletePromotionTags({ id: showModal.id });
                }
              }
            }
          ]}
          backgroundAction={() => setShowModal(null)}
        />
      </MainContentWrapper>
    </Layout>
  );
};

const NewPromotionTagWrapper = styled(Grid.Box)`
  padding: 0 15px;
  margin-bottom: 25px;
`;

const PromotionTagWrapper = styled(Grid.Box)`
  padding: 0 15px;
`;

const PromotionTagList = styled.div`
  margin-top: ${({ theme }) => theme.spacing.margins.xl};
`;

export default ViewAllPromotionalTagsPage;
